import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { StatusBadge } from "./StatusBadge";
import { DateFilterPicker } from "./DateFilterPicker";
import { formatDate } from "../../lib/dateUtils";

const STATUS_FILTERS = [
  { value: "", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "banned", label: "Banned" },
  { value: "deleted", label: "Deleted" },
];

export const StrangersTab = () => {
  const navigate = useNavigate();
  const [strangers, setStrangers] = useState(null);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = () => {
    api
      .get("/admin/strangers", { params: { status: status || undefined, search: search || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined } })
      .then(({ data }) => setStrangers(data));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, search, dateFrom, dateTo]);

  return (
    <div data-testid="admin-strangers-tab">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            data-testid={`stranger-status-filter-${f.value || "all"}`}
            onClick={() => setStatus(f.value)}
            className={`text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full border-2 transition-colors ${status === f.value ? "bg-stranger-orange text-clean-white border-stranger-orange" : "border-soft-gray text-midnight-black/70"}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <Input data-testid="stranger-search-input" placeholder="Search name, email, or user ID..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
        <DateFilterPicker label="Joined From" testId="stranger-date-from-input" value={dateFrom} onChange={setDateFrom} />
        <DateFilterPicker label="Joined To" testId="stranger-date-to-input" value={dateTo} onChange={setDateTo} />
      </div>

      {strangers === null ? (
        <p className="font-medium text-midnight-black/60">Loading strangers...</p>
      ) : strangers.length === 0 ? (
        <p className="font-medium text-midnight-black/60" data-testid="no-strangers">No strangers match.</p>
      ) : (
        <div className="space-y-3" data-testid="strangers-list">
          {strangers.map((s) => (
            <div
              key={s.id}
              className="border border-soft-gray rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap cursor-pointer hover:border-stranger-orange transition-colors"
              onClick={() => navigate(`/admin/strangers/${s.id}`)}
              data-testid={`stranger-row-${s.id}`}
            >
              <div className="flex-1 min-w-[220px] flex items-center gap-3">
                {s.has_profile_photo ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/profile-photo/${s.id}`}
                    alt={`${s.first_name} ${s.last_name}`}
                    className="w-10 h-10 rounded-full object-cover shrink-0 bg-warm-white"
                    data-testid={`stranger-thumbnail-${s.id}`}
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-soft-gray flex items-center justify-center font-bold text-xs shrink-0" data-testid={`stranger-thumbnail-placeholder-${s.id}`}>
                    {(s.first_name || "?")[0]}
                  </div>
                )}
                <div>
                <p className="font-extrabold text-sm uppercase">
                  {s.first_name} {s.last_name} {s.age ? <span className="text-midnight-black/40 font-medium">· {s.age}</span> : null}
                </p>
                <p className="text-xs font-medium text-midnight-black/60">{s.email} · {s.gender || "Not set"}</p>
                <p className="text-xs font-medium text-midnight-black/50">
                  Interested in: {s.session_preference || "Not set"} · {s.booking_count} booking{s.booking_count === 1 ? "" : "s"}
                </p>
                <p className="text-[10px] font-bold uppercase tracking-wide text-midnight-black/40 mt-1">Joined {formatDate(s.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!s.profile_completed && (
                  <span className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-soft-gray text-midnight-black/60" data-testid={`stranger-profile-incomplete-${s.id}`}>
                    Incomplete
                  </span>
                )}
                <StatusBadge status={s.status} testId={`stranger-status-${s.id}`} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
